<template>
  <section class="admin-content" id="vendor-employee-dashboard">
    <Navbar />
    <main class="admin-main">

      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center text-white" style="padding: 32px 0;">
            <div class="header-icon-wrap mr-3">
              <i class="fe fe-bar-chart-2" style="font-size: 22px;"></i>
            </div>
            <div>
              <h4 class="mb-0 font-weight-bold">{{ vendor ? vendor.name : "Vendor" }} Dashboard</h4>
              <small style="opacity: .8;">Track vendor orders, pending approvals, and stock pressure points</small>
            </div>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">

          <div v-if="vendor && vendor.status && vendor.status !== 'approved'" class="alert"
            :class="vendor.status === 'pending' ? 'alert-warning' : 'alert-danger'">
            <strong>Your business is {{ vendor.status }}.</strong>
            <span v-if="vendor.status === 'pending'">
              You can add products now; they appear in the marketplace once the marketplace team approves your account.
            </span>
            <span v-else-if="vendor.status_reason"> Reason: {{ vendor.status_reason }}</span>
          </div>

          <div class="d-flex flex-wrap mb-4" style="gap: 8px;">
            <router-link to="/vendor/products" class="btn btn-sm btn-submit"><i class="fe fe-package mr-1"></i>My products</router-link>
            <router-link to="/vendor/account" class="btn btn-sm btn-outline-primary"><i class="fe fe-users mr-1"></i>Business &amp; staff</router-link>
            <router-link v-if="vendor && vendor.status === 'approved'" :to="{ path: '/marketplace', query: { vendor: vendor.id } }"
              class="btn btn-sm btn-outline-primary"><i class="fe fe-external-link mr-1"></i>View my store</router-link>
          </div>

          <div class="row mb-4">
            <div class="col-md-4 col-6 mb-3" v-for="card in cards" :key="card.label">
              <div class="stat-card" :class="card.variant">
                <div class="stat-icon">
                  <i :class="'fe ' + card.icon"></i>
                </div>
                <div>
                  <p class="stat-label">{{ card.label }}</p>
                  <h3 class="stat-value">{{ card.value }}</h3>
                </div>
              </div>
            </div>
          </div>

          <div class="card shadow-sm table-card">
            <div class="d-flex align-items-center justify-content-between px-4 pt-4 pb-3">
              <div class="d-flex align-items-center">
                <i class="fe fe-shopping-cart text-primary mr-2"></i>
                <h6 class="mb-0 font-weight-bold">Recent Procurement Orders</h6>
              </div>
              <router-link to="/procurement/orders">
                <button class="btn btn-sm btn-submit"><i class="fe fe-external-link mr-1"></i>Open Orders</button>
              </router-link>
            </div>
            <div class="table-responsive">
              <table class="table align-td-middle mb-0">
                <thead style="background: #f8fafc;">
                  <tr>
                    <th class="table-th">Order #</th>
                    <th class="table-th">Provider</th>
                    <th class="table-th">Status</th>
                    <th class="table-th">Requested</th>
                    <th class="table-th">Approved</th>
                    <th class="table-th">Created</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="order in recentOrders" :key="order.id" class="table-row">
                    <td class="px-4 py-3">
                      <router-link :to="`/procurement/orders/${order.id}`" class="font-weight-bold text-primary">
                        {{ order.order_number }}
                      </router-link>
                    </td>
                    <td class="py-3">
                      {{ order.provider_user ? `${order.provider_user.firstname || ''} ${order.provider_user.lastname ||
                      ''}` : order.provider_user_id }}
                    </td>
                    <td class="py-3">
                      <span :class="'order-badge order-badge--' + (order.status || 'pending')">{{ order.status }}</span>
                    </td>
                    <td class="py-3"><i class="mdi mdi-currency-ngn"></i>{{ order.total_requested_amount }}</td>
                    <td class="py-3 text-success font-weight-bold"><i class="mdi mdi-currency-ngn"></i>{{
                      order.total_approved_amount }}</td>
                    <td class="py-3 text-muted small">{{ order.created_at }}</td>
                  </tr>
                  <tr v-if="!recentOrders.length">
                    <td colspan="6" class="text-center py-5 text-muted">
                      <i class="fe fe-inbox" style="font-size:2rem;opacity:.3"></i>
                      <p class="mt-2 mb-0">No recent orders</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </section>
    </main>
  </section>
</template>

<script>
  import Navbar from "@/views/Navbar.vue";

  export default {
    components: { Navbar },
    data() {
      return {
        vendor: "",
        summary: {
          total_orders: 0,
          submitted_orders: 0,
          approved_orders: 0,
          received_orders: 0,
          total_stock_lines: 0,
          low_stock_lines: 0,
        },
        recentOrders: [],
      };
    },
    computed: {
      cards() {
        return [
          { label: "Total Orders", value: this.summary.total_orders, icon: "fe-shopping-cart", variant: "stat-card--blue" },
          { label: "Submitted", value: this.summary.submitted_orders, icon: "fe-send", variant: "stat-card--purple" },
          { label: "Approved", value: this.summary.approved_orders, icon: "fe-check-circle", variant: "stat-card--green" },
          { label: "Received", value: this.summary.received_orders, icon: "fe-package", variant: "stat-card--teal" },
          { label: "Stock Lines", value: this.summary.total_stock_lines, icon: "fe-layers", variant: "stat-card--blue" },
          { label: "Low Stock", value: this.summary.low_stock_lines, icon: "fe-alert-triangle", variant: "stat-card--red" },
        ];
      },
    },
    beforeMount() {
      this.fetchDashboard();
    },
    methods: {
      fetchDashboard() {
        this.axios
          .get("/api/v1/auth/vendor-employee/dashboard")
          .then((res) => {
            this.vendor = res.data.vendor;
            this.summary = res.data.summary || this.summary;
            this.recentOrders = res.data.recent_orders || [];
          })
          .catch((err) => {
            this.$toasted.error((err.response && err.response.data && err.response.data.message) || "Unable to load dashboard", { position: "top-center", duration: 3000 });
          });
      },
    },
  };
</script>

<style scoped>
  .page-header {
    background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  }

  .header-icon-wrap {
    width: 48px;
    height: 48px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  /* Stat cards */
  .stat-card {
    display: flex;
    align-items: center;
    gap: 16px;
    border-radius: 14px;
    padding: 20px;
    color: #fff;
    box-shadow: 0 2px 12px rgba(0, 0, 0, .08);
  }

  .stat-card--blue {
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
  }

  .stat-card--purple {
    background: linear-gradient(135deg, #4c1d95, #7c3aed);
  }

  .stat-card--green {
    background: linear-gradient(135deg, #065f46, #10b981);
  }

  .stat-card--teal {
    background: linear-gradient(135deg, #134e4a, #0d9488);
  }

  .stat-card--red {
    background: linear-gradient(135deg, #7f1d1d, #ef4444);
  }

  .stat-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: rgba(255, 255, 255, .2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;
  }

  .stat-label {
    font-size: .78rem;
    opacity: .85;
    margin-bottom: 4px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: .04em;
  }

  .stat-value {
    font-size: 1.8rem;
    font-weight: 700;
    margin: 0;
    line-height: 1;
  }

  /* Table card */
  .table-card {
    border-radius: 12px;
    border: none;
  }

  .table-th {
    font-size: .73rem;
    text-transform: uppercase;
    letter-spacing: .04em;
    color: #64748b;
    font-weight: 600;
    border-bottom: 2px solid #e2e8f0;
    padding: 12px 16px;
  }

  .table-row:hover {
    background: #f8fafc;
  }

  /* Order status badges */
  .order-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: .75rem;
    font-weight: 600;
    text-transform: capitalize;
  }

  .order-badge--submitted {
    background: #dbeafe;
    color: #1d4ed8;
  }

  .order-badge--approved {
    background: #d1fae5;
    color: #065f46;
  }

  .order-badge--received {
    background: #d1fae5;
    color: #065f46;
  }

  .order-badge--pending {
    background: #fef3c7;
    color: #92400e;
  }

  .order-badge--rejected {
    background: #fee2e2;
    color: #991b1b;
  }

  .btn-submit {
    background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
    border: none;
    color: #fff;
    border-radius: 8px;
    font-weight: 600;
    padding: 8px 18px;
  }

  .btn-submit:hover {
    opacity: .9;
    color: #fff;
  }
</style>
